import { formatMoney } from "accounting"
import dayjs from "dayjs"

import { INCOGNITO_MASK } from "../../../../Components/Private"
import { baseOptions } from "../../../DashboardPage/components/chartsOptions"

const formatMoneyShort = (v) => {
  const n = Number(v) || 0
  if (Math.abs(n) >= 1000) return `$${(n / 1000).toFixed(1)}k`
  return `$${n.toFixed(0)}`
}

// Chart.js tick labels aren't React children - `<Private>` can't wrap them, so the axis
// callback itself branches on incognito and returns the mask string directly.
export const getUsageChartOptions = (theme, setTooltip, incognito) => ({
  ...baseOptions(theme),
  scales: {
    x: {
      ...baseOptions(theme).scales.x,
      ticks: {
        color: "#7A7A83",
        font: { size: 14, weight: 400 }
      }
    },
    y: {
      type: "linear",
      display: true,
      position: "left",
      beginAtZero: true,
      grid: {
        color: theme === "dark" ? "rgba(255, 255, 255, 0.12)" : "#E1E1E2",
        tickColor: "transparent"
      },
      border: { display: false, dash: [4, 4] },
      ticks: {
        maxTicksLimit: 6,
        font: { size: 14, weight: 400 },
        color: "#7A7A83",
        callback: (v) => (incognito ? INCOGNITO_MASK : formatMoneyShort(v))
      }
    }
  },
  plugins: {
    "vertical-line-plugin-with-points": { theme },
    tooltip: {
      enabled: false,
      external: (context) => {
        const tooltipModel = context.tooltip
        if (tooltipModel.opacity === 0) {
          setTooltip({ visible: false })
          if (context.chart.canvas._hideOnTouchOutside) {
            document.removeEventListener("touchstart", context.chart.canvas._hideOnTouchOutside)
            context.chart.canvas._hideOnTouchOutside = null
          }
          return
        }
        const position = context.chart.canvas.getBoundingClientRect()
        const dataPoint = context.tooltip.dataPoints?.[0]
        const dataIndex = dataPoint?.dataIndex

        const mainDataset = context.chart.data.datasets.find((d) => Array.isArray(d.mainLabels))
        const rawMainDate = mainDataset?.mainLabels?.[dataIndex] ?? dataPoint?.label

        const currentPoints = context.chart.data.datasets.map((dataset) => {
          const rawCompDate = dataset.comparisonLabels?.[dataIndex]
          return {
            value: dataset.data[dataIndex],
            datasetType: dataset.datasetType,
            backgroundColor: dataset.borderColor,
            label: dataset.label,
            dashed: Array.isArray(dataset.borderDash) && dataset.borderDash.length > 0,
            comparisonDate: rawCompDate ? dayjs(rawCompDate).format("MMM D, YYYY") : null
          }
        })

        setTooltip({
          visible: true,
          x: position.left + window.pageXOffset + tooltipModel.caretX,
          y: position.top + window.pageYOffset + tooltipModel.caretY,
          date: rawMainDate ? dayjs(rawMainDate).format("MMM D, YYYY") : null,
          data: currentPoints
        })

        if (!context.chart.canvas._hideOnTouchOutside) {
          const canvas = context.chart.canvas
          canvas._hideOnTouchOutside = (e) => {
            if (!canvas.contains(e.target)) {
              setTooltip({ visible: false })
              document.removeEventListener("touchstart", canvas._hideOnTouchOutside)
              canvas._hideOnTouchOutside = null
            }
          }
          document.addEventListener("touchstart", canvas._hideOnTouchOutside, { passive: true })
        }
      }
    },
    legend: { display: false }
  }
})

export const formatUsageMoney = (v) => formatMoney(Number(v) || 0, "$", 2, ",", ".")

export const datasetsOptions = {
  prevent: {
    label: "Prevent",
    borderColor: "#225AEA",
    borderWidth: 2,
    datasetType: "prevent"
  },
  deflect: {
    label: "Deflect",
    borderColor: "#DF7009",
    borderWidth: 2,
    datasetType: "deflect"
  },
  // Reserved for the dispute product; not rendered until it is billed.
  recover: {
    label: "Recover",
    borderColor: "#16895A",
    borderWidth: 2,
    datasetType: "recover"
  }
}
